import {
  Component,
  computed,
  contentChild,
  effect,
  input,
  output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Item } from '../../form/select-field/select-field.component';
import { CalendarTimelineComponent } from '../calendar-timeline/calendar-timeline.component';
import {
  CalendarToolbarComponent,
  CalendarToolbarViewOption,
} from '../calendar-toolbar/calendar-toolbar.component';
import { CalendarComponent } from '../calendar.component';
import { CalendarView } from '../models/calendar.model';
import { ResourceDayCalendarComponent } from '../resource-day-calendar/resource-day-calendar.component';

@Component({
  selector: 'siat-calendar-view',
  imports: [CalendarToolbarComponent],
  templateUrl: './calendar-view.component.html',
})
export class CalendarViewComponent {
  /**
   * A list of items to be rendered in the collaboratori select.
   */
  readonly collaboratoriItems = input<Item[]>([]);

  /**
   * The collaboratore form group.
   */
  readonly collaboratoreFormGroup = input<FormGroup | null>(null);

  /**
   * The collaboratore form control name.
   */
  readonly collaboratoreControlName = input<string>('collaboratore');

  /**
   * A flag to show the collaboratori row.
   */
  readonly showCollaboratoreRow = input<boolean>(true);

  /**
   * The view options to be rendered in the toolbar (the FIRST toggle
   * group — e.g. a "Vista Standard"/"Vista operatori" mode switch).
   */
  readonly viewOptions = input<CalendarToolbarViewOption[]>([]);

  /**
   * the view options to be rendered in the toolbar (the SECOND toggle
   * group — e.g. a "Mese"/"Settimana" period switch).
   */
  readonly standardViewTypes = input<CalendarToolbarViewOption[]>();
  private readonly resolvedStandardViewTypes = computed(
    () => this.standardViewTypes() ?? this.viewOptions()
  );

  /**
   * The current view mode.
   */
  readonly viewMode = input.required<string>();

  /**
   * The current primary toggle value.
   */
  readonly primaryToggleValue = input<string>();
  protected readonly resolvedPrimaryToggleValue = computed(
    () => this.primaryToggleValue() ?? this.viewMode()
  );

  /**
   * The secondary view options to be rendered in the toolbar (the SECOND toggle
   * group — e.g. a "Mese"/"Settimana" period switch).
   */
  readonly secondaryViewOptions = input<CalendarToolbarViewOption[]>([]);

  /**
   * The current secondary view mode.
   */
  readonly secondaryViewMode = input<string>('');

  /**
   * The resources value.
   */
  readonly resourcesValue = input<string>('resources');

  /**
   * The `viewMode` values that route navigation/title/etc. to the projected
   * `siat-calendar-timeline` instead of `siat-calendar`/resource-day.
   */
  readonly timelineViewValues = input<string[]>([]);

  /**
   * A flag to show the add button.
   */
  readonly showAddButton = input<boolean>(false);

  /**
   * The add button options.
   */
  readonly addButtonOptions = input<string[]>([]);

  /**
   * When true, the add button is rendered visibly disabled
   */
  readonly addButtonDisabled = input<boolean>(false);

  /**
   * Emit The view mode change
  . */
  readonly viewModeChange = output<string>();

  /**
   * Emitted when the secondary toggle group changes.
   */
  readonly secondaryViewModeChange = output<string>();

  /*
   * Emit the add new event
   */
  readonly addNew = output<string>();

  /**
   * The calendar reference.
   * {@link CalendarComponent}
   */
  private readonly calendarRef = contentChild(CalendarComponent);

  /**
   * The resource day reference.
   * {@link ResourceDayCalendarComponent}
   */
  private readonly resourceDayRef = contentChild(ResourceDayCalendarComponent);

  /**
   * The resource-timeline reference.
   * {@link CalendarTimelineComponent}
   */
  private readonly timelineRef = contentChild(CalendarTimelineComponent);

  /**
   * A flag to show the resources mode.
   */
  readonly isResourcesMode = computed(
    () => this.viewMode() === this.resourcesValue()
  );

  /**
   * A flag to show the resource-timeline mode.
   */
  readonly isTimelineMode = computed(() =>
    this.timelineViewValues().includes(this.viewMode())
  );

  /**
   * A flag to show the collaboratori select.
   */
  readonly showCollaboratoreSelect = computed(
    () =>
      this.showCollaboratoreRow() &&
      !this.isResourcesMode() &&
      !this.isTimelineMode()
  );

  /**
   * The granularity of the date picker.
   */
  readonly datePickerGranularity = computed<'day' | 'month'>(() =>
    this.viewMode().toLowerCase().includes('month') ? 'month' : 'day'
  );

  /**
   * The current period label.
   */
  readonly currentPeriodLabel = computed(() => {
    if (this.isResourcesMode())
      return this.resourceDayRef()?.formattedDate() ?? '';
    if (this.isTimelineMode()) return this.timelineRef()?.currentTitle() ?? '';
    return this.calendarRef()?.currentTitle() ?? '';
  });

  /**
   * True while there are any "Prestazione" events on the projected calendar.
   */
  readonly hasPrestazioneEvents = computed(
    () =>
      this.calendarRef()?.hasPrestazioneEvents() ??
      this.timelineRef()?.hasPrestazioneEvents() ??
      false
  );

  /**
   * True while "Focus Prestazione" is active on the projected calendar.
   */
  readonly focusPrestazione = computed(
    () =>
      this.calendarRef()?.focusPrestazione() ??
      this.timelineRef()?.focusPrestazione() ??
      false
  );

  /**
   * Toggles "Focus Prestazione" on the projected calendar.
   */
  toggleFocusPrestazione(): void {
    this.calendarRef()?.toggleFocusPrestazione();
    this.timelineRef()?.toggleFocusPrestazione();
  }

  /**
   * Explicitly sets "Focus Prestazione" on the projected calendar. No-op in
   * resources mode, where the toggle isn't available.
   */
  setFocusPrestazione(value: boolean): void {
    this.calendarRef()?.setFocusPrestazione(value);
    this.timelineRef()?.setFocusPrestazione(value);
  }

  /**
   * Forces the projected `siat-calendar` to re-measure its container (see
   * `CalendarComponent.refreshSize`). No-op in resources mode, which isn't
   * FullCalendar-based and doesn't need this.
   */
  refreshSize(): void {
    this.calendarRef()?.refreshSize();
    this.timelineRef()?.refreshSize();
  }

  /**
   * Sync the calendar view.
   * @returns { EffectRef }
   */
  private readonly _syncCalendarView = effect(() => {
    if (this.isResourcesMode() || this.isTimelineMode()) return;
    const mode = this.viewMode();
    const matchedOption = this.resolvedStandardViewTypes().find(
      o => o.value === mode
    );
    this.calendarRef()?.setView({
      type: (matchedOption?.viewType ?? mode) as CalendarView['type'],
      label: matchedOption?.label ?? mode,
    });
  });

  /**
   *  On calendar navigation.
   * @param direction
   * @returns { void }
   */
  onCalendarNav(direction: 'prev' | 'next' | 'today'): void {
    if (this.isResourcesMode()) {
      this.resourceDayRef()?.navigate(direction);
      return;
    }
    const ref = this.isTimelineMode() ? this.timelineRef() : this.calendarRef();
    if (!ref) return;
    if (direction === 'prev') ref.prev();
    else if (direction === 'next') ref.next();
    else ref.goToday();
  }

  /**
   * Go to a specific date.
   * @param date
   */
  goToSpecificDate(date: Date | null): void {
    if (!date) return;
    if (this.isResourcesMode()) {
      this.resourceDayRef()?.jumpToDate(date);
      return;
    }
    if (this.isTimelineMode()) {
      this.timelineRef()?.gotoDate(date);
      return;
    }
    this.calendarRef()?.gotoDate(date);
  }
}
